import { useState } from "react";
import * as Icons from "lucide-react";
import FieldRenderer from "./FieldRenderer";
import BlockPicker from "./BlockPicker";
import { BLOCK_TYPES, makeBlock, getBlockLabel } from "@/lib/blockTypes";

function Icon({ name, ...rest }) {
  const key = (name || "").split("-").map((w) => w[0]?.toUpperCase() + w.slice(1)).join("");
  const C = Icons[key] || Icons.Sparkles;
  return <C {...rest} />;
}

/**
 * Section editor: a list of blocks with add/remove/reorder + per-block field forms.
 *
 * props:
 *   section  { key, title, order, blocks: [{key,type,data,order}] }
 *   onChange fn(newSection)
 *   onRemove fn()
 *   onMoveUp / onMoveDown  optional
 *   sectionIndex, totalSections
 */
export default function SectionEditor({
  section, onChange, onRemove, onDuplicate, sectionIndex, totalSections, dragHandleProps,
}) {
  const [collapsed, setCollapsed] = useState(false);
  const [openBlockKey, setOpenBlockKey] = useState(section.blocks?.[0]?.key || null);
  const [showPicker, setShowPicker] = useState(false);

  const blocks = section.blocks || [];

  const setBlocks = (newBlocks) =>
    onChange({ ...section, blocks: newBlocks.map((b, i) => ({ ...b, order: i })) });

  const addBlock = (type) => {
    const nb = makeBlock(type);
    setBlocks([...blocks, nb]);
    setOpenBlockKey(nb.key);
    setShowPicker(false);
  };
  const removeBlock = (idx) => {
    const nb = blocks.filter((_, i) => i !== idx);
    setBlocks(nb);
    if (openBlockKey === blocks[idx].key) setOpenBlockKey(nb[0]?.key || null);
  };
  const moveBlock = (from, to) => {
    if (to < 0 || to >= blocks.length) return;
    const arr = [...blocks];
    [arr[from], arr[to]] = [arr[to], arr[from]];
    setBlocks(arr);
  };
  const patchBlock = (idx, patch) => {
    const arr = [...blocks];
    arr[idx] = { ...arr[idx], ...patch };
    setBlocks(arr);
  };

  return (
    <div
      className="border border-[#E5E7EB] rounded-xl bg-white overflow-hidden"
      data-testid={`section-${section.key}`}
    >
      {/* Section header */}
      <div className="bg-[#F8FAF7] border-b border-[#E5E7EB] p-4 flex items-center gap-3">
        {dragHandleProps ? <button type="button" {...dragHandleProps} /> : null}
        <button
          type="button"
          onClick={() => setCollapsed(!collapsed)}
          data-testid={`section-${section.key}-toggle`}
          className="p-1 text-gray-500 hover:text-gray-900"
        >
          <Icons.ChevronDown size={16} className={`transition-transform ${collapsed ? "-rotate-90" : ""}`} />
        </button>
        <input
          data-testid={`section-${section.key}-title`}
          value={section.title || ""}
          onChange={(e) => onChange({ ...section, title: e.target.value })}
          placeholder="Section title"
          className="font-display font-semibold text-gray-900 bg-transparent border-none outline-none flex-1 focus:ring-0"
        />
        <input
          data-testid={`section-${section.key}-key`}
          value={section.key}
          onChange={(e) => onChange({ ...section, key: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })}
          className="text-xs bg-white border border-gray-200 rounded px-2 py-1 font-mono w-40 text-gray-600"
          title="Section key (URL slug for section endpoint)"
        />
        <div className="flex items-center gap-1">
          {onDuplicate && (
            <button type="button" onClick={onDuplicate}
              title="Duplicate section"
              data-testid={`section-${section.key}-duplicate`}
              className="p-1.5 text-gray-500 hover:text-[#09582D]"
            ><Icons.Copy size={14} /></button>
          )}
          <button type="button" onClick={onRemove}
            title="Delete section"
            data-testid={`section-${section.key}-remove`}
            className="p-1.5 text-gray-500 hover:text-[#DC2626]"
          ><Icons.Trash2 size={14} /></button>
        </div>
      </div>

      {!collapsed && (
        <div className="p-5 space-y-3">
          {blocks.length === 0 && (
            <div className="text-center py-6 text-sm text-gray-400">No blocks yet.</div>
          )}

          {blocks.map((b, i) => {
            const spec = BLOCK_TYPES[b.type];
            const isOpen = openBlockKey === b.key;
            return (
              <div
                key={b.key}
                className={`border rounded-lg transition-colors ${
                  isOpen ? "border-[#538135]" : "border-[#E5E7EB]"
                }`}
                data-testid={`block-${b.key}`}
              >
                {/* Block header */}
                <div
                  className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer ${
                    isOpen ? "bg-[#EAF4E3]/30" : "hover:bg-gray-50"
                  }`}
                  onClick={() => setOpenBlockKey(isOpen ? null : b.key)}
                >
                  <div className="w-8 h-8 rounded-md bg-white border border-[#E5E7EB] flex items-center justify-center text-[#538135]">
                    <Icon name={spec?.icon || "sparkles"} size={14} strokeWidth={1.75} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 truncate">
                      {getBlockLabel(b.type)}
                    </div>
                    <div className="text-[11px] text-gray-500 font-mono">{b.type}</div>
                  </div>
                  <div className="flex items-center gap-0.5" onClick={(e) => e.stopPropagation()}>
                    <button type="button" onClick={() => moveBlock(i, i - 1)} disabled={i === 0}
                      data-testid={`block-${b.key}-up`}
                      className="p-1.5 text-gray-400 hover:text-gray-700 disabled:opacity-30"><Icons.ArrowUp size={12} /></button>
                    <button type="button" onClick={() => moveBlock(i, i + 1)} disabled={i === blocks.length - 1}
                      data-testid={`block-${b.key}-down`}
                      className="p-1.5 text-gray-400 hover:text-gray-700 disabled:opacity-30"><Icons.ArrowDown size={12} /></button>
                    <button type="button" onClick={() => removeBlock(i)}
                      data-testid={`block-${b.key}-remove`}
                      className="p-1.5 text-gray-400 hover:text-[#DC2626]"><Icons.Trash2 size={12} /></button>
                  </div>
                </div>

                {/* Block fields */}
                {isOpen && spec && (
                  <div className="p-4 border-t border-[#E5E7EB]">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3 pb-3 border-b border-[#E5E7EB]">
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1.5">Block key (unique)</label>
                        <input
                          data-testid={`block-${b.key}-key`}
                          value={b.key}
                          onChange={(e) => patchBlock(i, { key: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })}
                          className="w-full font-mono text-xs border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#EAF4E3] focus:border-[#538135]"
                        />
                      </div>
                    </div>
                    {(spec.fields || []).map((f) => (
                      <FieldRenderer
                        key={f.key}
                        field={f}
                        value={b.data?.[f.key]}
                        onChange={(v) => patchBlock(i, { data: { ...(b.data || {}), [f.key]: v } })}
                        path={b.key}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          <button
            type="button" onClick={() => setShowPicker(true)}
            data-testid={`section-${section.key}-add-block`}
            className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:border-[#538135] hover:text-[#09582D] hover:bg-[#EAF4E3]/30 transition-colors font-medium"
          >
            + Add block
          </button>
          {showPicker && <BlockPicker onPick={addBlock} onCancel={() => setShowPicker(false)} />}
        </div>
      )}
    </div>
  );
}
